import React, { createContext, useContext, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Global "where did I come from" stack. Every real route change pushes the
// new location on top; Back/Close buttons pop the previous one instead of
// navigating to a page-specific hardcoded route. Falls back to a caller-given
// default when the stack has nothing before the current page (e.g. the app
// was opened directly on that page). Does not touch the browser's own
// back/forward buttons — those still use native history.
const NavigationHistoryContext = createContext(null);

const MAX_STACK = 50;

export function NavigationHistoryProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const stackRef = useRef([]);

  useEffect(() => {
    const current = location.pathname + location.search;
    const stack = stackRef.current;
    if (stack[stack.length - 1] === current) return;
    stack.push(current);
    if (stack.length > MAX_STACK) stack.shift();
  }, [location.pathname, location.search]);

  const goBack = useCallback((fallbackPath) => {
    const stack = stackRef.current;
    const current = location.pathname + location.search;
    if (stack[stack.length - 1] === current) stack.pop();
    const prev = stack.pop();
    if (prev) navigate(prev);
    else navigate(fallbackPath || "/dashboard");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location.pathname, location.search]);

  return (
    <NavigationHistoryContext.Provider value={{ goBack }}>
      {children}
    </NavigationHistoryContext.Provider>
  );
}

export function useNavigationHistory() {
  const ctx = useContext(NavigationHistoryContext);
  if (!ctx) throw new Error("useNavigationHistory must be used within a NavigationHistoryProvider");
  return ctx;
}
